import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ChevronsUpDown,
  Search,
  Check,
  Plus,
  UserCircle2,
  HelpCircle,
  FileText,
  LogOut,
  Pin,
  PinOff,
  ShieldAlert,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { PortalPopover } from '../ui/PortalPopover';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Organization, UserOrganizationSummary } from '../../types/domain';

export function getOrgInitials(name?: string | null): string {
  if (!name || !name.trim()) return 'OR';
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 1) {
    return words[0].slice(0, 2).toUpperCase();
  }
  const first = words[0][0];
  const last = words[words.length - 1][0];
  return (first + last).toUpperCase();
}

export interface OrganizationSelectorProps {
  collapsed?: boolean;
  isPinned?: boolean;
  onTogglePin?: () => void;
  mobileOpen?: boolean;
  onCloseMobile?: () => void;
  onToggleCollapse?: () => void;
  onOpenCreateOrg?: () => void;
  onDropdownOpenChange?: (isOpen: boolean) => void;
}

export const OrganizationSelector: React.FC<OrganizationSelectorProps> = ({
  collapsed = false,
  isPinned = true,
  onTogglePin,
  mobileOpen = false,
  onCloseMobile,
  onToggleCollapse,
  onOpenCreateOrg,
  onDropdownOpenChange,
}) => {
  const navigate = useNavigate();
  const { currentOrg, organizations, switchOrganization, user, logout } = useAuth();
  const { showToast } = useToast();

  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const triggerRef = useRef<HTMLButtonElement>(null);

  const handleSetOpen = (open: boolean) => {
    setIsOpen(open);
    onDropdownOpenChange?.(open);
  };

  const filteredOrgs = organizations.filter((org) =>
    org.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSelectOrg = async (org: UserOrganizationSummary | Organization) => {
    handleSetOpen(false);
    setSearchTerm('');
    await switchOrganization(org as Organization);
    navigate(`/organization/${org.id}`);
    onCloseMobile?.();
  };

  const handleOpenCreate = () => {
    handleSetOpen(false);
    setSearchTerm('');
    onOpenCreateOrg?.();
  };

  const handleNavigate = (path: string) => {
    handleSetOpen(false);
    setSearchTerm('');
    navigate(path);
    onCloseMobile?.();
  };

  const handleReplayTour = () => {
    handleSetOpen(false);
    setSearchTerm('');
    showToast({
      type: 'info',
      title: 'Guided Tour',
      message: 'Welcome to Ericius! Navigate your organizations, datastores, and ingest endpoints from the sidebar.',
    });
    onCloseMobile?.();
  };

  const handleLogout = async () => {
    handleSetOpen(false);
    setSearchTerm('');
    await logout();
    navigate('/login');
    onCloseMobile?.();
  };

  const handlePinClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onTogglePin) {
      onTogglePin();
    } else if (onToggleCollapse) {
      onToggleCollapse();
    }
  };

  const isCollapsedDesktop = collapsed && !mobileOpen;
  const activeOrg = currentOrg || organizations[0] || null;
  const currentOrgName = activeOrg?.name || 'Organization';
  const initials = getOrgInitials(currentOrgName);

  return (
    <div className="h-12 px-2.5 border-b border-cf-border flex items-center justify-between flex-shrink-0 relative w-full select-none">
      {/* Selector Trigger Button */}
      <Button
        ref={triggerRef as any}
        type="button"
        variant="ghost"
        onClick={() => handleSetOpen(!isOpen)}
        className={`flex items-center gap-2.5 py-1 rounded-lg hover:bg-cf-surfaceHover transition-colors focus:outline-none text-left cursor-pointer min-w-0 h-auto ${
          isCollapsedDesktop ? 'justify-center w-full px-0' : 'flex-1 px-1.5 md:mr-1'
        }`}
        title={isCollapsedDesktop ? currentOrgName : 'Switch organization'}
        aria-label="Switch organization"
      >
        {/* Organization Avatar Badge */}
        <div className="w-7 h-7 rounded-md bg-[#2563eb] text-white font-bold text-xs flex items-center justify-center shrink-0 shadow-sm">
          {initials}
        </div>

        {/* Organization Title & Caret (Hidden when collapsed on desktop) */}
        {!isCollapsedDesktop && (
          <div className="flex items-center justify-between min-w-0 flex-1 gap-1">
            <span className="font-semibold text-xs tracking-tight text-cf-textPrimary truncate">
              {currentOrgName}
            </span>
            <ChevronsUpDown size={13} className="text-cf-textMuted shrink-0" />
          </div>
        )}
      </Button>

      {/* Right-side Pin Button (Desktop only, hidden on mobile) */}
      {!isCollapsedDesktop && (
        <div className="hidden md:flex items-center shrink-0">
          <div className="h-5 w-px bg-cf-border shrink-0 mr-1" />
          <Button
            type="button"
            variant="ghost"
            size="icon-xs"
            onClick={handlePinClick}
            className={`shrink-0 ${
              isPinned
                ? 'text-blue-500 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-300 hover:bg-blue-500/10'
                : 'text-cf-textMuted hover:text-cf-textPrimary'
            }`}
            title={isPinned ? 'Unpin sidebar (auto-collapse)' : 'Pin sidebar (keep open)'}
            aria-label={isPinned ? 'Unpin sidebar' : 'Pin sidebar'}
          >
            {isPinned ? (
              <Pin size={16} className="fill-blue-500/20" />
            ) : (
              <PinOff size={16} />
            )}
          </Button>
        </div>
      )}

      {/* Organization Selector Dropdown Popover */}
      <PortalPopover
        isOpen={isOpen}
        onClose={() => {
          handleSetOpen(false);
          setSearchTerm('');
        }}
        triggerRef={triggerRef}
        align="left"
        className="w-72 bg-white dark:bg-[#1e1f29] bg-cf-surface border border-cf-border rounded-xl shadow-2xl overflow-hidden p-2 text-xs"
      >
        {/* Search Bar */}
        <div className="mb-2">
          <Input
            type="text"
            size="xs"
            leftIcon={<Search size={14} className="text-blue-500 shrink-0" />}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && filteredOrgs.length > 0) {
                e.preventDefault();
                handleSelectOrg(filteredOrgs[0]);
              }
            }}
            placeholder="Search"
            autoFocus
          />
        </div>

        {/* Organizations List */}
        <div className="max-h-48 overflow-y-auto space-y-0.5 no-scrollbar py-0.5">
          {filteredOrgs.map((org) => {
            const isSelected = activeOrg ? org.id === activeOrg.id : false;
            return (
              <Button
                key={org.id}
                type="button"
                variant="ghost"
                fullWidth
                onClick={() => handleSelectOrg(org)}
                className={`flex items-center justify-between px-2.5 py-2 rounded-lg hover:bg-cf-surfaceHover text-left transition-colors group cursor-pointer h-auto ${
                  isSelected ? 'bg-cf-surfaceActive/60 font-semibold' : ''
                }`}
              >
                <div className="flex items-center gap-2.5 min-w-0 flex-1">
                  <div className="w-7 h-7 rounded-md bg-[#2563eb] text-white font-bold text-xs flex items-center justify-center shrink-0">
                    {getOrgInitials(org.name)}
                  </div>
                  <span className="text-xs text-cf-textPrimary truncate">{org.name}</span>
                </div>
                {isSelected && <Check size={16} className="text-cf-textPrimary shrink-0 ml-2" />}
              </Button>
            );
          })}

          {filteredOrgs.length === 0 && (
            <div className="px-3 py-2.5 text-center text-xs text-cf-textMuted">
              No organizations found
            </div>
          )}
        </div>

        {/* Action: Add new organization */}
        <Button
          type="button"
          variant="ghost"
          fullWidth
          onClick={handleOpenCreate}
          className="flex items-center justify-start gap-2.5 px-2.5 py-2 rounded-lg hover:bg-cf-surfaceHover text-left transition-colors text-xs font-medium text-cf-textPrimary mt-0.5 h-auto cursor-pointer"
        >
          <div className="w-7 h-7 rounded-md border border-cf-border bg-cf-bg flex items-center justify-center shrink-0 text-cf-textSecondary">
            <Plus size={14} />
          </div>
          <span>Add new organization</span>
        </Button>

        {/* Separator Divider */}
        <div className="my-1.5 border-t border-cf-border" />

        {/* User Account Section */}
        <div className="px-2.5 py-1 text-xs text-cf-textMuted font-medium truncate">
          {user?.email || 'user@example.com'}
        </div>

        <div className="space-y-0.5">
          <Button
            type="button"
            variant="ghost"
            fullWidth
            onClick={() => handleNavigate('/account')}
            className="flex items-center justify-start gap-2.5 px-2.5 py-1.5 rounded-lg hover:bg-cf-surfaceHover text-left transition-colors text-xs font-medium text-cf-textPrimary h-auto cursor-pointer"
          >
            <UserCircle2 size={16} className="text-cf-textSecondary shrink-0" />
            <span>My settings</span>
          </Button>

          <Button
            type="button"
            variant="ghost"
            fullWidth
            onClick={handleReplayTour}
            className="flex items-center justify-start gap-2.5 px-2.5 py-1.5 rounded-lg hover:bg-cf-surfaceHover text-left transition-colors text-xs font-medium text-cf-textPrimary h-auto cursor-pointer"
          >
            <HelpCircle size={16} className="text-cf-textSecondary shrink-0" />
            <span>Replay guided tour</span>
          </Button>

          <Button
            type="button"
            variant="ghost"
            fullWidth
            onClick={() => handleNavigate('/account/billing')}
            className="flex items-center justify-start gap-2.5 px-2.5 py-1.5 rounded-lg hover:bg-cf-surfaceHover text-left transition-colors text-xs font-medium text-cf-textPrimary h-auto cursor-pointer"
          >
            <FileText size={16} className="text-cf-textSecondary shrink-0" />
            <span>Subscriptions</span>
          </Button>

          {user?.is_superadmin && (
            <Button
              type="button"
              variant="ghost"
              fullWidth
              onClick={() => handleNavigate('/admin')}
              className="flex items-center justify-start gap-2.5 px-2.5 py-1.5 rounded-lg hover:bg-rose-500/10 text-left transition-colors text-xs font-medium text-rose-600 dark:text-rose-400 h-auto cursor-pointer"
            >
              <ShieldAlert size={16} className="shrink-0 text-rose-500" />
              <span>Platform Admin</span>
            </Button>
          )}

          <Button
            type="button"
            variant="ghost"
            fullWidth
            onClick={handleLogout}
            className="flex items-center justify-start gap-2.5 px-2.5 py-1.5 rounded-lg hover:bg-cf-surfaceHover text-left transition-colors text-xs font-medium text-cf-textPrimary h-auto cursor-pointer"
          >
            <LogOut size={16} className="text-cf-textSecondary shrink-0" />
            <span>Log out</span>
          </Button>
        </div>
      </PortalPopover>
    </div>
  );
};
